<template>
    <div class="firm loading-content-container">
        <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>
        <b-row>
            <csp-desktop-firm-basedata :cols="8" :form-context="initialFormContexts.firmBasedata"/>
            <csp-desktop-firm-functions :cols="4" :form-context="initialFormContexts.firmFunctions"
                @saved="handleFirmFunctionsSaved"/>
        </b-row>
        <b-row class="mt-4">
            <csp-desktop-firm-accounting :cols="8" :form-context="initialFormContexts.firmAccounting"/>
            <csp-desktop-firm-bank-account :cols="4" :form-context="initialFormContexts.firmBankAccount"/>
        </b-row>
        <b-row class="mt-4">
            <csp-desktop-firm-assignments ref="firmAssignments" :cols="8"
                :form-context="initialFormContexts.firmAssignments"/>
            <csp-desktop-firm-appraisers :cols="4" :form-context="initialFormContexts.firmAppraisers"/>
        </b-row>
        <b-row class="mt-4">
            <csp-desktop-firm-presets :cols="8" :form-context="initialFormContexts.firmPresets"/>
            <csp-desktop-firm-record-process :cols="4" :form-context="initialFormContexts.firmRecordProcess"/>
            <!-- we never have recordProcess (only for carDealers) and carDealerAssignments (only for appraisers)
            in the same firm available -->
            <b-col v-if="showAssignedCarDealer" cols="4">
                <csp-desktop-car-dealer-assignments
                    :items-ajax-url="`/api/desktop/firms/${firmId}/car-dealer-assignments`"/>
            </b-col>
        </b-row>
        <b-row class="mt-4">
            <csp-desktop-firm-remarks :cols="4" :form-context="initialFormContexts.firmRemarks"/>
            <csp-desktop-firm-documents :cols="4" :form-context="initialFormContexts.firmDocuments"/>
            <csp-desktop-firm-files :cols="4" :form-context="initialFormContexts.firmFiles"/>
        </b-row>
        <b-row class="mt-4 mb-4">
            <csp-desktop-firm-configurable-documents :cols="8" :context-id="firmId" context-type="firm"
                :form-context="initialFormContexts.firmConfigurableDocuments"/>
        </b-row>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import FormContext from '@desktop/components/FormContext';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import FirmBasedata from '@desktop/components/firm/detail/FirmBasedata.vue';
    import FirmAccounting from '@desktop/components/firm/detail/FirmAccounting.vue';
    import FirmAppraisers from '@desktop/components/firm/detail/FirmAppraisers.vue';
    import FirmAssignments from '@desktop/components/firm/detail/FirmAssignments.vue';
    import FirmBankAccount from '@desktop/components/firm/detail/FirmBankAccount.vue';
    import FirmDocuments from '@desktop/components/firm/detail/FirmDocuments.vue';
    import FirmFiles from '@desktop/components/firm/detail/FirmFiles.vue';
    import FirmFunctions from '@desktop/components/firm/detail/FirmFunctions.vue';
    import FirmPresets from '@desktop/components/firm/detail/FirmPresets.vue';
    import FirmRecordProcess from '@desktop/components/firm/detail/FirmRecordProcess.vue';
    import FirmRemarks from '@desktop/components/firm/detail/FirmRemarks.vue';
    import CarDealerAssignments from '@desktop/components/firm/CarDealerAssignments.vue';
    import FirmConfigurableDocuments from '@desktop/components/FirmConfigurableDocuments.vue';

    const container = Container.getInstance();
    const firmRepository = container.getDesktopFirmRepository();

    export default {
        name: 'FirmDetails',
        components: {
            'csp-desktop-firm-basedata': FirmBasedata,
            'csp-desktop-firm-accounting': FirmAccounting,
            'csp-desktop-firm-appraisers': FirmAppraisers,
            'csp-desktop-firm-assignments': FirmAssignments,
            'csp-desktop-firm-bank-account': FirmBankAccount,
            'csp-desktop-firm-documents': FirmDocuments,
            'csp-desktop-firm-files': FirmFiles,
            'csp-desktop-firm-functions': FirmFunctions,
            'csp-desktop-firm-presets': FirmPresets,
            'csp-desktop-firm-record-process': FirmRecordProcess,
            'csp-desktop-firm-remarks': FirmRemarks,
            'csp-desktop-car-dealer-assignments': CarDealerAssignments,
            'csp-desktop-firm-configurable-documents': FirmConfigurableDocuments,
        },
        mixins: [FormDataMixin],
        data() {
            const firmId = parseInt(this.$route.params.firmId);
            return {
                firmId,
                isLoading: true,
                initialFormContexts: {
                    firmBasedata: new FormContext({
                        name: 'firmBasedata',
                        objectId: firmId,
                        reloadFields: ['firmGroup', 'isFleetCustomer', 'isMajorCustomer'],
                    }),
                    firmAccounting: new FormContext({
                        name: 'firmAccounting',
                        objectId: firmId,
                    }),
                    firmAppraisers: new FormContext({
                        name: 'firmAppraisers',
                        objectId: firmId,
                    }),
                    firmAssignments: new FormContext({
                        name: 'firmAssignments',
                        objectId: firmId,
                    }),
                    firmBankAccount: new FormContext({
                        name: 'firmBankAccount',
                        objectId: firmId,
                    }),
                    firmDocuments: new FormContext({
                        name: 'firmDocuments',
                        objectId: firmId,
                    }),
                    firmFiles: new FormContext({
                        name: 'firmFiles',
                        objectId: firmId,
                    }),
                    firmFunctions: new FormContext({
                        name: 'firmFunctions',
                        objectId: firmId,
                    }),
                    firmPresets: new FormContext({
                        name: 'firmPresets',
                        objectId: firmId,
                    }),
                    firmRecordProcess: new FormContext({
                        name: 'firmRecordProcess',
                        objectId: firmId,
                    }),
                    firmRemarks: new FormContext({
                        name: 'firmRemarks',
                        objectId: firmId,
                    }),
                    firmConfigurableDocuments: new FormContext({
                        name: 'firmConfigurableDocuments',
                        objectId: firmId,
                    }),
                },
                showAssignedCarDealer: false,
            }
        },
        created() {
            const loadFunction = () => {
                return firmRepository.loadFirm(this.firmId);
            }

            this.loadFormData(loadFunction)
                .then((data) => {
                    if (data.data.meta.permissions?.canShowAppraiserFirmCarDealerAssignments) {
                        this.showAssignedCarDealer = true;
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        methods: {
            handleFirmFunctionsSaved() {
                this.$refs.firmAssignments.reload();
            },
        },
    }
</script>
